import React, { useState, useEffect } from 'react';

import {
    Box,
    Typography,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
    Skeleton,
    TableContainer,
    Menu,
    MenuItem,
    Button,
    Tooltip

} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import ToggleSwitch from '../../../components/ToggleSwitch/ToggleSwitch';

import ConfirmChanges from '../../../components/Projects/ConfirmChanges';

const projects = [
    {
        field: 'Approve timesheets',
        headline: false,
        toggle: false,
        contentTootltop: false,
    },
    {
        field: 'Approve time off requests',
        headline: false,
        toggle: false,
        contentTootltop: false,
    },
    {
        field: 'Schedule shifts',
        headline: false,
        toggle: false,
        contentTootltop: false,
    },
    {
        field: 'Weekly time limit notifications',
        headline: false,
        toggle: false,
        contentTootltop: false,
    },
    {
        field: 'Receive work break issue notifications',
        headline: false,
        toggle: false,
        contentTootltop: false,
    },
    {
        field: 'Create and remove team projects',
        headline: false,
        toggle: false,
        contentTootltop: false,
    },
    {
        field: 'Edit team members and project roles',
        headline: 'Invite members to the organization',
        toggle: <ToggleSwitch />,
        contentTootltop: false,
    },
    {
        field: 'Receive job entry/exit notifications',
        headline: false,
        toggle: false,
        contentTootltop: false,
    },
    {
        field: 'View screenshots/activities for other members',
        headline: false,
        toggle: false,
        contentTootltop: false,
    },
    {
        field: 'Manage financials',
        headline: false,
        toggle: false,
        contentTootltop: <Tooltip title="• View/Edit pay/bill rates for team members on the team projects
• Can't: View pay/bill rates in reports (coming soon)
• Can't: View Manage payroll page or add team members to payroll
• Can't: View Past payments page
• Can't: View Create payments page or create payroll adjustments
• Can't: Create client invoices
• Can't: Generate team invoices for other members" arrow placement="top" alignContent="center">
            <button className="tooltipBtn ms-2 border-0 bg-transparent p-0">
                <InfoOutlineIcon fontSize="small" />
            </button>
        </Tooltip>,
    },
];

const CustomCheckbox = ({ checked, onChange }) => (
    <Checkbox
        checked={checked}
        onChange={onChange}
        icon={
            <Box
                sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    border: '1px solid #D0D5DD',
                    backgroundColor: '#E5E8EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M20 6L9 17L4 12"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Box>
        }
        checkedIcon={
            <Box
                sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    backgroundColor: '#1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M20 6L9 17L4 12"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Box>
        }
    />
);

const TeamPermissionsTable = () => {
    const [loading, setLoading] = useState(true);
    const [selectedValues, setSelectedValues] = useState([]);


    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 1000);
        return () => clearTimeout(timer);
    }, []);

    const handleCheckboxChange = (name) => {
        setSelectedValues((prev) =>
            prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
        );
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedValues(projects.map((p) => p.field));
        } else {
            setSelectedValues([]);
        }
    };
    const colors = ['#D60087', '#0057FF', '#CC6B00']; // magenta, blue, orange
    const [openConfirmChanges, setOpenConfirmChanges] = useState(false);

    return (
        <div>
            <TableContainer sx={{ boxShadow: 'none' }}>
                <Table className='shadow-none' sx={{ boxShadow: 'none', borderCollapse: 'separate', }}>
                    <TableHead>
                        <TableRow sx={{
                            backgroundColor: '#DFDFDF',
                            '& th:first-of-type': { borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' },
                            '& th:last-of-type': { borderTopRightRadius: '8px', borderBottomRightRadius: '8px' },
                        }}>
                            <TableCell sx={{ fontFamily: 'Manrope', color: '#626262', py: '2px' }} padding="checkbox">
                                <CustomCheckbox
                                    checked={projects.length > 0 && selectedValues.length === projects.length}
                                    onChange={handleSelectAll}
                                />
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'Manrope', color: '#626262', py: '2px', fontSize: '14px', fontWeight: '500', padding: '0px 16px', height: '50px', lineHeight: '0' }}>#</TableCell>
                            <TableCell sx={{ fontFamily: 'Manrope', color: '#626262', py: '2px', fontSize: '14px', fontWeight: '500', padding: '0px 16px', height: '50px', lineHeight: '0' }}>Team lead permissions</TableCell>
                            <TableCell sx={{ fontFamily: 'Manrope', color: '#626262', py: '2px', fontSize: '14px', fontWeight: '500', padding: '0px 16px', height: '50px', lineHeight: '0' }} align='center'>
                                <Box display="flex" alignItems="center" justifyContent="center" sx={{ gap: '12px' }}>Teams
                                    <Tooltip title="Teams where Team leads have the selected permissions ON" arrow placement="top" alignContent="center">
                                        <button className="tooltipBtn ms-2 border-0 bg-transparent p-0">
                                            <InfoOutlineIcon fontSize="small" sx={{ fontSize: '14px' }} />
                                        </button>
                                    </Tooltip>
                                </Box>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'Manrope', color: '#626262', py: '2px', fontSize: '14px', fontWeight: '500', padding: '0px 16px', height: '50px', lineHeight: '0' }} align='center'>
                                Only notify team lead</TableCell>

                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ backgroundColor: '#fff' }}>
                        {loading
                            ? [...Array(11)].map((_, i) => (
                                <TableRow key={i}>
                                    {[...Array(5)].map((_, j) => (
                                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                            : projects.map((project, index) => (
                                <TableRow key={index} sx={{
                                    borderRadius: '8px',
                                    '& td:first-of-type': { borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' },
                                    '& td:last-of-type': { borderTopRightRadius: '8px', borderBottomRightRadius: '8px' },
                                }}>
                                    <TableCell sx={{ borderBottom: 'none', whiteSpace: 'nowrap', py: '10px' }} padding="checkbox">
                                        <CustomCheckbox
                                            checked={selectedValues.includes(project.field)}
                                            onChange={() => handleCheckboxChange(project.field)}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: 'none', whiteSpace: 'nowrap', py: '10px', fontFamily: 'Manrope' }}>{index + 1}</TableCell>
                                    <TableCell sx={{ borderBottom: 'none', whiteSpace: 'nowrap', py: '10px', fontFamily: 'Manrope' }}>
                                        <Box component="a" href='#' onClick={() => setOpenConfirmChanges(true)}>
                                            <Typography display="flex" alignItems="center" sx={{ gap: '12px', lineHeight: '1', fontSize: '16px', fontWeight: '600', color: '#1A1F36', fontFamily: 'Manrope' }}>{project.field}<span>{project.contentTootltop}</span></Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography display="flex" alignItems="center" sx={{ fontSize: '14px', fontWeight: '400', color: '#8C8C8C', fontFamily: 'Manrope' }}>{project.headline}</Typography>
                                            <Typography sx={{ fontSize: '14px', fontWeight: '400', color: '#8C8C8C', fontFamily: 'Manrope' }}>{project.toggle}</Typography>
                                        </Box>

                                    </TableCell>
                                    <TableCell sx={{ borderBottom: 'none', whiteSpace: 'nowrap', py: '10px' }}>




                                        <Box display="flex" alignItems="center" justifyContent="center">
                                            <Box sx={{ display: 'flex', position: 'relative', mr: '10px' }}>
                                                {colors.map((color, index) => (
                                                    <Avatar
                                                        key={index}
                                                        sx={{
                                                            backgroundColor: color,
                                                            width: '40px',
                                                            height: '40px',
                                                            fontSize: '20px',
                                                            position: 'relative',
                                                            border: '1px solid #fff',
                                                            zIndex: colors.length - index,
                                                            //   ml: index === 0 ? 0 : -1.2,
                                                            ml: index === 0 ? 0 : '-18px',

                                                        }}
                                                    >
                                                        <Groups2RoundedIcon sx={{ color: 'white', fontSize: '20px' }} />
                                                    </Avatar>
                                                ))}
                                            </Box>
                                            <Typography sx={{ fontSize: 14, color: '#595959', fontWeight: '700' }}>5</Typography>
                                        </Box>




                                    </TableCell>
                                    <TableCell sx={{ borderBottom: 'none', whiteSpace: 'nowrap', fontFamily: 'Manrope' }} align='center'>
                                        <ToggleSwitch />
                                    </TableCell>

                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <ConfirmChanges open={openConfirmChanges} onClose={() => setOpenConfirmChanges(false)} />
        </div>
    )
}

export default TeamPermissionsTable
